import React, { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MagnifyingGlass, Sparkle, Fire, CalendarBlank, ArrowRight, X } from "@phosphor-icons/react";
import EventCard from "../../components/EventCard";
import { PageLoader, EmptyState, SmartImage } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import api, { money } from "../../lib/api";
import { useConfig } from "../../lib/useConfig";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { RefreshBar } from "../../components/RefreshBar";

const SHORTCUTS = [
  { label: "Today", type: "date", value: "today" },
  { label: "Tomorrow", type: "date", value: "tomorrow" },
  { label: "This Weekend", type: "date", value: "weekend" },
  { label: "Next 7 Days", type: "date", value: "next7" },
];

function Row({ title, overline, events }) {
  if (!events?.length) return null;
  return (
    <section className="mt-12">
      <div className="flex items-end justify-between mb-4">
        <div>
          {overline && <div className="overline mb-1">{overline}</div>}
          <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight">{title}</h2>
        </div>
      </div>
      <div className="flex gap-5 overflow-x-auto hide-scrollbar pb-2 -mx-1 px-1">
        {events.map((e) => (
          <div key={e.id} className="w-[45%] sm:w-[240px] shrink-0"><EventCard event={e} /></div>
        ))}
      </div>
    </section>
  );
}

export default function Discovery() {
  const { city } = useOutletContext();
  const { genres } = useConfig();
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [genre, setGenre] = useState(sp.get("genre") || "all");
  const [dateFilter, setDateFilter] = useState("");
  const [results, setResults] = useState(null);

  useEffect(() => {
    const g = sp.get("genre");
    if (g) setGenre(g);
  }, [sp]);

  const loadDiscovery = () => api.get(`/events/discovery?city=${encodeURIComponent(city)}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  const { refreshing, lastUpdated, refresh } = useAutoRefresh(loadDiscovery, [city]);

  const filtering = q || genre !== "all" || dateFilter;

  useEffect(() => {
    if (!filtering) { setResults(null); return; }
    const params = new URLSearchParams({ city });
    if (q) params.set("q", q);
    if (genre !== "all") params.set("genre", genre);
    if (dateFilter) params.set("date_filter", dateFilter);
    const t = setTimeout(() => {
      api.get(`/events?${params.toString()}`).then((r) => setResults(r.data.events));
    }, 300);
    return () => clearTimeout(t);
  }, [q, genre, dateFilter, city, filtering]);

  const featured = data?.featured?.[0];
  const rest = useMemo(() => (data?.featured || []).slice(1, 5), [data]);

  const clearFilters = () => { setQ(""); setGenre("all"); setDateFilter(""); };

  if (loading) return <PageLoader label="Loading events" />;

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 py-6">
      <RefreshBar refreshing={refreshing} lastUpdated={lastUpdated} onRefresh={refresh} />
      {/* AI + search bar */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="discovery-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search events in ${city}…`} className="pl-11 h-12 rounded-full bg-card" />
        </div>
        <Button asChild className="h-12 rounded-full px-6" data-testid="discovery-ask-ai">
          <Link to="/ask-ai"><Sparkle weight="fill" className="mr-2" /> Ask AI to find an event</Link>
        </Button>
      </div>

      {/* filter chips */}
      <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar pb-2">
        {SHORTCUTS.map((s) => (
          <button key={s.value} data-testid={`chip-${s.value}`}
            onClick={() => setDateFilter(dateFilter === s.value ? "" : s.value)}
            className={`shrink-0 inline-flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-medium border transition-colors ${dateFilter === s.value ? "bg-foreground text-background border-foreground" : "bg-card border-border hover:border-foreground/40"}`}>
            <CalendarBlank size={15} /> {s.label}
          </button>
        ))}
        <div className="w-px h-6 bg-border mx-1 shrink-0" />
        <Select value={genre} onValueChange={setGenre}>
          <SelectTrigger className="w-[150px] h-9 rounded-full bg-card shrink-0" data-testid="genre-filter"><SelectValue placeholder="Genre" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All genres</SelectItem>
            {genres.map((g) => <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {filtering && (
          <button onClick={clearFilters} className="shrink-0 inline-flex items-center gap-1 text-sm text-primary px-2" data-testid="clear-filters"><X size={14} /> Clear</button>
        )}
      </div>

      {/* results (filtered) */}
      {filtering ? (
        <div className="mt-8">
          <div className="overline mb-3">{results ? `${results.length} results` : "Searching…"}</div>
          {results && results.length === 0 ? (
            <EmptyState icon={MagnifyingGlass} title="No events found" subtitle="Try a different date, genre or search term." testid="no-results" />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {(results || []).map((e) => <EventCard key={e.id} event={e} />)}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* featured bento */}
          {featured && (
            <section className="mt-6 grid lg:grid-cols-12 gap-5">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-7">
                <Link to={`/events/${featured.id}`} data-testid="featured-hero" className="group block relative rounded-xl overflow-hidden">
                  <SmartImage src={featured.banner} alt={featured.title} ratio="wide" rounded="rounded-xl" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent z-[2]" />
                  <div className="absolute bottom-0 left-0 p-6 md:p-8 z-[3] text-white">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary text-white text-xs font-semibold mb-3"><Fire weight="fill" size={13} /> Featured</span>
                    <h2 className="font-heading text-2xl md:text-4xl font-semibold tracking-tight max-w-lg leading-tight">{featured.title}</h2>
                    <div className="mt-2 text-white/80 text-sm">{featured.city} · {money(featured.min_price)} onwards</div>
                  </div>
                </Link>
              </motion.div>
              <div className="lg:col-span-5 grid grid-cols-2 gap-5">
                {rest.map((e) => <EventCard key={e.id} event={e} />)}
              </div>
            </section>
          )}

          {/* categories */}
          <section className="mt-12">
            <div className="overline mb-3">Browse by category</div>
            <div className="flex gap-3 overflow-x-auto hide-scrollbar pb-2">
              {genres.map((g) => (
                <button key={g.id} onClick={() => setGenre(g.name)} data-testid={`cat-${g.name}`}
                  className="shrink-0 px-5 h-11 rounded-full border border-border bg-card font-medium hover:border-foreground/40 btn-press"
                  style={{ borderLeftColor: g.color, borderLeftWidth: 3 }}>
                  {g.name}
                </button>
              ))}
            </div>
          </section>

          <Row overline="Hot right now" title="Trending in your city" events={data?.trending} />
          <Row overline="This weekend" title="Perfect for the weekend" events={data?.weekend} />
          <Row overline="Coming up" title="Next 7 days" events={data?.next7} />

          {(!data || data.total === 0) && (
            <div className="mt-10">
              <EmptyState icon={CalendarBlank} title={`No events in ${city} yet`} subtitle="Try changing your city from the top bar." testid="no-events" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
